import { isValidGtin } from "@logiful/domain";
import { Package, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api, ApiError, type ProductItem } from "@/lib/api";
import { usePermission, useTenant } from "./AppShell";
import { Button, Input } from "./ui";

import { t, msg } from "@/lib/i18n";
type Row = { name: string; quantity: string; ean: string };
const toRows = (p: ProductItem): Row[] => (p.packUnits ?? []).map((u) => ({ name: u.name, quantity: String(u.quantity), ean: u.ean ?? "" }));

/**
 * Gebinde am Artikel (z. B. „Karton 20“, „Palette 480“) mit eigener EAN – gehen ans Lager (FFN „Gebinde“), gelten bei
 * B2B für „nur ganze Kartons“ und zeigen Mengen als „2 × Karton 20“. Aus Odoo kommen sie aus den Verpackungen.
 */
export function PackUnitsSection({ p, onSaved }: { p: ProductItem; onSaved: () => void }) {
  const { tenantId } = useTenant();
  const editable = usePermission()("products");
  const [rows, setRows] = useState<Row[]>(() => toRows(p));
  const [saving, setSaving] = useState(false);
  const dirty = JSON.stringify(rows) !== JSON.stringify(toRows(p));
  const problems = rows.map((r) => {
    const q = Number(r.quantity);
    if (!r.name.trim()) return "Name fehlt";
    if (!Number.isInteger(q) || q < 2) return "Menge ab 2";
    if (r.ean && (!/^\d{8,14}$/.test(r.ean) || !isValidGtin(r.ean))) return "EAN ungültig";
    if (r.ean && r.ean === p.identifiers.gtin) return "gleiche EAN wie der Artikel";
    return null;
  });
  const save = async () => {
    setSaving(true);
    try {
      await api.products.patch(tenantId, p.id, { packUnits: rows.map((r) => ({ name: r.name.trim(), quantity: Number(r.quantity), ean: r.ean.trim() || null })).sort((a, b) => a.quantity - b.quantity) });
      toast.success("Gebinde gespeichert", { description: msg("Werden ans Lager übertragen.") });
      onSaved();
    } catch (e) {
      toast.error(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Nicht gespeichert") : "Nicht gespeichert");
    } finally {
      setSaving(false);
    }
  };
  const set = (i: number, patch: Partial<Row>) => setRows((xs) => xs.map((x, k) => (k === i ? { ...x, ...patch } : x)));

  return (
    <section>
      <div className="flex items-baseline mb-1">
        <h3 className="text-[12px] font-semibold text-fg-muted uppercase tracking-wide flex items-center gap-1.5"><Package className="size-3.5" /> {t("Gebinde")}</h3>
      </div>
      <p className="text-[12px] text-fg-subtle mb-2.5">{t("Verpackungseinheiten mit eigener EAN (z. B. Karton, Palette) – fürs Lager, B2B („nur ganze Kartons“) und Einkauf.")}</p>
      {rows.length > 0 && (
        <ul className="space-y-1.5 mb-2">
          {rows.map((r, i) => (
            <li key={i} className="grid grid-cols-[1fr_80px_150px_28px] gap-1.5 items-start">
              <Input value={r.name} onChange={(e) => set(i, { name: e.target.value })} placeholder={t("Name, z. B. Karton 20")} disabled={!editable} className="h-8 text-[13px]" aria-label={t("Name des Gebindes")} />
              <Input value={r.quantity} onChange={(e) => set(i, { quantity: e.target.value.replace(/\D/g, "") })} placeholder={t("Stk")} disabled={!editable} className="h-8 text-[13px] text-center" aria-label={t("Stück je Gebinde")} />
              <Input value={r.ean} onChange={(e) => set(i, { ean: e.target.value.replace(/\D/g, "") })} placeholder={t("EAN (optional)")} disabled={!editable} className="h-8 text-[13px] font-mono" aria-label={t("EAN des Gebindes")} />
              {editable && <button type="button" onClick={() => setRows((xs) => xs.filter((_, k) => k !== i))} className="size-8 grid place-items-center rounded-md text-fg-subtle hover:text-danger hover:bg-surface-2" aria-label={t("Gebinde entfernen")}><Trash2 className="size-3.5" /></button>}
              {problems[i] && <span className="col-span-4 text-[11.5px] text-danger">{problems[i]}</span>}
            </li>
          ))}
        </ul>
      )}
      {editable && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="ghost" icon={<Plus className="size-3.5" />} onClick={() => setRows((xs) => [...xs, { name: "", quantity: "", ean: "" }])} disabled={rows.length >= 20}>{t("Gebinde")}</Button>
          {dirty && <Button size="sm" variant="primary" loading={saving} disabled={problems.some(Boolean)} onClick={() => void save()}>{t("Speichern")}</Button>}
        </div>
      )}
      {!rows.length && !editable && <p className="text-[12.5px] text-fg-muted">{t("Keine Gebinde gepflegt.")}</p>}
    </section>
  );
}
